// deps
import React, { useCallback } from "react";

// internal-deps
import { RouteContext } from "@swiggy-private/core-ui";
import { DineoutCheckoutRoute as CheckoutRoute } from "@swiggy-private/checkout-types";

// hooks
import { useNavigate } from "@checkout-core/hooks/UseNavigate";

// reducers
import { CheckoutActionTypes } from "@checkout-core/reducers/Checkout";

// contexts
import { CheckoutContext } from "@checkout-core/contexts/CheckoutContext";
import { HardwareBackContext } from "@checkout-core/contexts/HardwareBackContext";

export function DineoutCheckoutRouteContainer(props: {
    children: React.ReactElement | null;
}): React.ReactElement | null {
    const { checkoutState } = React.useContext(CheckoutContext);
    const { setState: setHardwareBackState } = React.useContext(HardwareBackContext);
    const navigateTo = useNavigate();
    const { route } = React.useContext(RouteContext);
    const type = checkoutState.type;

    const redirectToPaymentPage = React.useMemo(() => {
        if (checkoutState.type === CheckoutActionTypes.PROCESS_ORDER) {
            return checkoutState.payload.webviewRefreshExtraParams?.redirectToPaymentPageWhenWebviewRefreshed;
        }
        return false;
    }, [checkoutState]);

    const handleBack = useCallback(
        (event: PopStateEvent): void => {
            // if route is cart, then close the webview
            if (route?.name === CheckoutRoute.TABLE_RESERVATION_CART) {
                history.pushState({ name: CheckoutRoute.TABLE_RESERVATION_CART, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user is on timer page
            if (route?.name === CheckoutRoute.VPA_TIMER) {
                history.pushState({ name: CheckoutRoute.VPA_TIMER, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user is on home page and want to go back
            else if (route?.name === CheckoutRoute.PAYMENTS) {
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // coupon listing page landing
            else if (route?.name === CheckoutRoute.COUPON_LISTING) {
                history.pushState({ name: CheckoutRoute.COUPON_LISTING, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // dineout success page
            else if (route?.name === CheckoutRoute.DINEOUT_PAYMENT_SUCCESS) {
                history.pushState({ name: CheckoutRoute.DINEOUT_PAYMENT_SUCCESS, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user is on payment txn status poller page and want to go back
            else if (route?.name === CheckoutRoute.PAYMENT_TXN_STATUS_POLLING) {
                history.pushState({ name: CheckoutRoute.PAYMENT_TXN_STATUS_POLLING, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            // handle scenario when user on confirm order state do not navigate
            else if (
                route?.name === CheckoutRoute.CONFIRM_ORDER ||
                route?.name === CheckoutRoute.DINEOUT_DIRECT_PLACE_ORDER
            ) {
                history.pushState({ name: route?.name, data: route?.data }, "", "");
                return;
            }

            // handle scenario when user has linked the wallet and want to go back
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            else if (route?.name === CheckoutRoute.VERIFY_WALLET_LINK_OTP && !!(route?.data as any)?.stopNavigation) {
                history.pushState({ name: route?.name, data: route?.data }, "", "");
                return;
            }

            // dine cash pooling page
            else if (route?.name === CheckoutRoute.DINE_CASH_POOLING) {
                history.pushState({ name: CheckoutRoute.DINE_CASH_POOLING, data: route?.data }, "", "");
                setHardwareBackState({
                    shouldHandle: true,
                    activeRoute: route?.name,
                });
                return;
            }

            if (event.state && event.state.name) {
                navigateTo(event.state.name, event.state.data);
            }
        },
        [navigateTo, route?.data, route?.name, setHardwareBackState],
    );

    React.useEffect(() => {
        window.addEventListener("popstate", handleBack);

        return (): void => {
            window.removeEventListener("popstate", handleBack);
        };
    }, [handleBack]);

    React.useEffect(() => {
        if (type === CheckoutActionTypes.SHOW_PAYMENTS) {
            if (
                [
                    CheckoutRoute.PAYMENTS,
                    CheckoutRoute.ADD_NEW_CARD,
                    CheckoutRoute.LINK_PAYLATER,
                    CheckoutRoute.LINK_PAYLATER_MOBILE,
                    CheckoutRoute.VERIFY_WALLET_LINK_OTP,
                    CheckoutRoute.LINK_WALLET_WITH_NUMBER,
                    CheckoutRoute.PAYMENTS_INDEX,
                    CheckoutRoute.MORE_BANKS,
                    CheckoutRoute.VERIFY_VPA,
                    CheckoutRoute.PAYMENT_TXN_STATUS_POLLING,
                    CheckoutRoute.MORE_PAYMENT_GROUPS,
                    CheckoutRoute.MORE_PAYMENT_METHODS,
                    CheckoutRoute.CART,
                    CheckoutRoute.PAYMENT_OFFERS,
                    CheckoutRoute.DINE_CASH_POOLING,
                ].indexOf(route?.name as CheckoutRoute) === -1
            ) {
                navigateTo(CheckoutRoute.PAYMENTS);
                return;
            }
        }

        if (type === CheckoutActionTypes.SHOW_FULL_PAGE_LOADER) {
            route?.name !== CheckoutRoute.REDIRECT_PAGE && navigateTo(CheckoutRoute.REDIRECT_PAGE);
            return;
        }

        if (type === CheckoutActionTypes.POST_SWIGGY_ADD_MONEY || type === CheckoutActionTypes.POST_PAYLATER_LINKING) {
            route?.name !== CheckoutRoute.PAYMENTS && navigateTo(CheckoutRoute.PAYMENTS);
        }

        if (type === CheckoutActionTypes.DINEOUT_PAYMENT_SUCCESS) {
            route?.name !== CheckoutRoute.DINEOUT_PAYMENT_SUCCESS && navigateTo(CheckoutRoute.DINEOUT_PAYMENT_SUCCESS);
        }

        if (type === CheckoutActionTypes.DINEOUT_DIRECT_PLACE_ORDER) {
            route?.name !== CheckoutRoute.DINEOUT_DIRECT_PLACE_ORDER &&
                navigateTo(CheckoutRoute.DINEOUT_DIRECT_PLACE_ORDER);
        }

        /** if Checkout state if vpa timer */
        if (type === CheckoutActionTypes.VPA_TIMER) {
            route?.name !== CheckoutRoute.VPA_TIMER && navigateTo(CheckoutRoute.VPA_TIMER);
            return;
        }

        /** if Checkout state is process order and webview refresh has happened then user would be redirected to payment page */
        if (redirectToPaymentPage) {
            route?.name !== CheckoutRoute.PAYMENTS && navigateTo(CheckoutRoute.PAYMENTS);
            return;
        }

        /** if Checkout state if upi intent poller */
        if (type === CheckoutActionTypes.PAYMENT_TXN_STATUS_POLLER) {
            route?.name !== CheckoutRoute.PAYMENT_TXN_STATUS_POLLING &&
                navigateTo(CheckoutRoute.PAYMENT_TXN_STATUS_POLLING);
            return;
        }

        /** if Checkout state if confirm order */
        if (type === CheckoutActionTypes.CONFIRM_ORDER) {
            route?.name !== CheckoutRoute.CONFIRM_ORDER && navigateTo(CheckoutRoute.CONFIRM_ORDER);
        }

        if (type === CheckoutActionTypes.DINEOUT_BILL_PAYMENT_CART) {
            if (route?.name !== CheckoutRoute.BILL_PAYMENT_CART && route?.name !== CheckoutRoute.DINE_CASH_POOLING) {
                navigateTo(CheckoutRoute.BILL_PAYMENT_CART);
            }
            return;
        } else if (type === CheckoutActionTypes.DINEOUT_TABLE_RESERVATION_CART) {
            route?.name !== CheckoutRoute.TABLE_RESERVATION_CART && navigateTo(CheckoutRoute.TABLE_RESERVATION_CART);
            return;
        } else if (type === CheckoutActionTypes.SCENES_FULL_PAYMENT) {
            route?.name !== CheckoutRoute.SCENES_FULL_PAYMENT && navigateTo(CheckoutRoute.SCENES_FULL_PAYMENT);
            return;
        } else if (type === CheckoutActionTypes.STEAL_STORE_CART) {
            route?.name !== CheckoutRoute.STEAL_STORE_CART && navigateTo(CheckoutRoute.STEAL_STORE_CART);
            return;
        } else if (type === CheckoutActionTypes.SHOW_COUPON_LISTING) {
            route?.name !== CheckoutRoute.COUPON_LISTING && navigateTo(CheckoutRoute.COUPON_LISTING);
            return;
        }
    }, [type, navigateTo, route, redirectToPaymentPage]);

    return props.children;
}
